import { ProductHistoryListResponseDto } from '../api/models/ProductHistoryListResponseDto';

/**
 * Event type color mapping for visual indicators
 * Based on backend recommendations
 */
export const EVENT_TYPE_COLORS: Record<ProductHistoryListResponseDto.eventType, string> = {
  SALE_CREATED: '#10B981', // Green
  RESERVATION_CONFIRMED: '#10B981', // Green
  TRANSFER_CREATED: '#3B82F6', // Blue
  TRANSFER_COMPLETED: '#3B82F6', // Blue
  PRICE_CHANGED: '#F59E0B', // Orange
  RESERVATION_CREATED: '#8B5CF6', // Purple
  RESERVATION_RELEASED: '#8B5CF6', // Purple
  RESERVATION_EXPIRED: '#8B5CF6', // Purple
  STATUS_CHANGED: '#6B7280', // Gray
  AVAILABILITY_STATUS_CHANGED: '#6B7280', // Gray
  LOCATION_CHANGED: '#14B8A6', // Teal
  UNIT_ATTRIBUTE_CHANGED: '#6366F1', // Indigo
  PRODUCT_CATALOG_UPDATED: '#EAB308', // Yellow
  INVENTORY_REGISTERED: '#06B6D4', // Cyan
  PAYMENT_RECEIVED: '#059669', // Emerald
  PAYMENT_PROCESSED: '#10B981', // Green - payment processed
  TRANSFER_CANCELLED: '#EF4444', // Red
  REQUEST_CREATED: '#6366F1', // Indigo
  REQUEST_RESOLVED: '#4F46E5', // Indigo
  // Mixed-branch Sale Fulfillment Events
  SALE_FULFILLMENT_STARTED: '#6366F1', // Indigo
  SALE_FULFILLMENT_RECEIVED: '#0EA5E9', // Sky Blue
  SALE_FULFILLMENT_COMPLETED: '#10B981', // Green
  SALE_FULFILLMENT_FAILED: '#DC2626', // Red
  // Refund/Return Events
  ORDER_REFUNDED: '#DC2626', // Red - full refund
  ORDER_PARTIALLY_REFUNDED: '#F97316', // Orange - partial refund
  REFUND_PROCESSED: '#F97316', // Orange - refund processed
  ITEM_RETURNED: '#FB7185', // Rose - item returned
  // Exchange Events
  EXCHANGE_PROCESSED: '#8B5CF6', // Purple - exchange processed
  // Customer handoff / delivery
  ORDER_DELIVERY_UPDATED: '#0EA5E9', // Sky - delivery to customer
  PRODUCT_CREATED: '#22C55E', // Green - product birth
  PRODUCT_DELETED: '#DC2626', // Red - product removal
  // Inventory Record Lifecycle
  INVENTORY_RECORD_CREATED: '#0EA5E9', // Sky Blue - record creation
  INVENTORY_RECORD_UPDATED: '#8B5CF6', // Purple - record update
  INVENTORY_RECORD_COMPLETED: '#10B981', // Green - record approved
  INVENTORY_RECORD_CANCELLED: '#F97316', // Orange - record cancelled
  INVENTORY_RECORD_DELETED: '#DC2626', // Red - record deleted
  // Property Events
  PROPERTY_ADDED: '#22C55E', // Green - property added
  PROPERTY_UPDATED: '#F59E0B', // Orange - property updated
  PROPERTY_DELETED: '#DC2626', // Red - property deleted
  SAFETY_STOCK_UPDATED_INDIVIDUAL: '#0D9488', // Teal
  SAFETY_STOCK_UPDATED_BULK: '#0891B2', // Cyan
  PHYSICAL_COUNT_FINALIZED: '#7C3AED', // Violet
  ROLE_CREATED: '#2563EB', // Blue
  ROLE_UPDATED: '#0EA5E9', // Sky
  ROLE_DELETED: '#DC2626', // Red
  ROLE_PERMISSIONS_UPDATED: '#4F46E5', // Indigo
  USER_CREATED: '#16A34A', // Green
  USER_UPDATED: '#0284C7', // Sky
  USER_DELETED: '#DC2626', // Red
  USER_ROLES_UPDATED: '#7C3AED', // Violet
  CUSTOMER_CREATED: '#059669', // Emerald
  CUSTOMER_UPDATED: '#0284C7', // Sky
  CUSTOMER_DELETED: '#DC2626', // Red
  LOGIN_SUCCESS: '#10B981', // Green
  LOGIN_FAILED: '#EF4444', // Red
  ACCOUNT_LOCKED: '#DC2626', // Red
  ACCOUNT_UNLOCKED: '#22C55E', // Green
  PASSWORD_RESET_BY_ADMIN: '#F59E0B', // Orange
  PASSWORD_CHANGED: '#3B82F6', // Blue
  FORCED_PASSWORD_CHANGE: '#8B5CF6', // Purple
};

/**
 * Label for SALE_FULFILLMENT_RECEIVED depends on whether all remote groups arrived.
 */
export const getSaleFulfillmentReceivedLabel = (newValue?: Record<string, unknown>): string =>
  newValue?.allGroupsReceived === true ? 'Venta lista para completar' : 'Piezas remotas recibidas';

/** List-row label helper when `newValue` is not included in list API responses. */
export const getEventTypeLabelForListItem = (
  item: Pick<
    ProductHistoryListResponseDto,
    'eventType' | 'relatedEntityType' | 'allGroupsReceived'
  >,
): string => {
  if (item.eventType === ProductHistoryListResponseDto.eventType.SALE_FULFILLMENT_RECEIVED) {
    return getSaleFulfillmentReceivedLabel(
      item.allGroupsReceived === true ? { allGroupsReceived: true } : undefined,
    );
  }
  return getEventTypeLabel(item.eventType, item.relatedEntityType);
};

/**
 * Get human-readable label for event type
 */
export const getEventTypeLabel = (
  eventType: ProductHistoryListResponseDto.eventType,
  relatedEntityType?: string,
  newValue?: Record<string, unknown>,
): string => {
  if (eventType === ProductHistoryListResponseDto.eventType.SALE_FULFILLMENT_RECEIVED) {
    return getSaleFulfillmentReceivedLabel(newValue);
  }
  if (
    eventType === ProductHistoryListResponseDto.eventType.SAFETY_STOCK_UPDATED_BULK &&
    relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION'
  ) {
    return 'Stock de seguridad (por selección)';
  }
  if (
    eventType === ProductHistoryListResponseDto.eventType.SAFETY_STOCK_UPDATED_BULK &&
    relatedEntityType === 'SAFETY_STOCK_BULK'
  ) {
    return 'Stock de seguridad (edición personalizada)';
  }

  const labels: Record<ProductHistoryListResponseDto.eventType, string> = {
    INVENTORY_REGISTERED: 'Inventario Registrado',
    PRODUCT_CREATED: 'Producto Creado',
    PRODUCT_DELETED: 'Producto Eliminado',
    INVENTORY_RECORD_CREATED: 'Registro Creado',
    INVENTORY_RECORD_UPDATED: 'Registro Actualizado',
    INVENTORY_RECORD_COMPLETED: 'Registro Completado',
    INVENTORY_RECORD_CANCELLED: 'Registro Cancelado',
    INVENTORY_RECORD_DELETED: 'Registro Eliminado',
    TRANSFER_CREATED: 'Transferencia Creada',
    TRANSFER_COMPLETED: 'Transferencia Completada',
    TRANSFER_CANCELLED: 'Transferencia Cancelada',
    REQUEST_CREATED: 'Solicitud creada',
    REQUEST_RESOLVED: 'Solicitud resuelta',
    SALE_FULFILLMENT_STARTED: 'Venta multi-sucursal iniciada',
    SALE_FULFILLMENT_RECEIVED: 'Piezas remotas recibidas',
    SALE_FULFILLMENT_COMPLETED: 'Venta multi-sucursal completada',
    SALE_FULFILLMENT_FAILED: 'Venta multi-sucursal cancelada',
    RESERVATION_CREATED: 'Reserva Creada',
    RESERVATION_CONFIRMED: 'Reserva Confirmada',
    RESERVATION_RELEASED: 'Reserva Liberada',
    RESERVATION_EXPIRED: 'Reserva Expirada',
    SALE_CREATED: 'Venta Creada',
    PAYMENT_RECEIVED: 'Pago Recibido',
    PAYMENT_PROCESSED: 'Pago Procesado',
    ORDER_REFUNDED: 'Venta reembolsada',
    ORDER_PARTIALLY_REFUNDED: 'Reembolso Parcial',
    REFUND_PROCESSED: 'Reembolso Procesado',
    ITEM_RETURNED: 'Pieza devuelta',
    EXCHANGE_PROCESSED: 'Intercambio Procesado',
    ORDER_DELIVERY_UPDATED: 'Entrega al cliente',
    STATUS_CHANGED: 'Estado Cambiado',
    AVAILABILITY_STATUS_CHANGED: 'Cambio de Disponibilidad',
    LOCATION_CHANGED: 'Ubicación Cambiada',
    PRICE_CHANGED: 'Precio Cambiado',
    UNIT_ATTRIBUTE_CHANGED: 'Atributo de pieza cambiado',
    PRODUCT_CATALOG_UPDATED: 'Catálogo Actualizado',
    PROPERTY_ADDED: 'Propiedad Agregada',
    PROPERTY_UPDATED: 'Propiedad Actualizada',
    PROPERTY_DELETED: 'Propiedad Eliminada',
    SAFETY_STOCK_UPDATED_INDIVIDUAL: 'Stock de seguridad (producto)',
    SAFETY_STOCK_UPDATED_BULK: 'Stock de seguridad (masivo)',
    PHYSICAL_COUNT_FINALIZED: 'Control de Inventario Fisico Completado',
    ROLE_CREATED: 'Rol Creado',
    ROLE_UPDATED: 'Rol Actualizado',
    ROLE_DELETED: 'Rol Eliminado',
    ROLE_PERMISSIONS_UPDATED: 'Permisos de Rol Actualizados',
    USER_CREATED: 'Usuario Creado',
    USER_UPDATED: 'Usuario Actualizado',
    USER_DELETED: 'Usuario Eliminado',
    USER_ROLES_UPDATED: 'Roles de Usuario Actualizados',
    CUSTOMER_CREATED: 'Cliente Creado',
    CUSTOMER_UPDATED: 'Cliente Actualizado',
    CUSTOMER_DELETED: 'Cliente Eliminado',
    LOGIN_SUCCESS: 'Inicio de sesión exitoso',
    LOGIN_FAILED: 'Inicio de sesión fallido',
    ACCOUNT_LOCKED: 'Cuenta bloqueada',
    ACCOUNT_UNLOCKED: 'Cuenta desbloqueada',
    PASSWORD_RESET_BY_ADMIN: 'Contraseña restablecida por admin',
    PASSWORD_CHANGED: 'Contraseña cambiada',
    FORCED_PASSWORD_CHANGE: 'Cambio de contraseña obligatorio',
  };
  return labels[eventType] || eventType;
};

/** Account-security events (document-level, no product row). */
export const ACCOUNT_SECURITY_EVENT_TYPES: ReadonlySet<ProductHistoryListResponseDto.eventType> =
  new Set([
    ProductHistoryListResponseDto.eventType.LOGIN_SUCCESS,
    ProductHistoryListResponseDto.eventType.LOGIN_FAILED,
    ProductHistoryListResponseDto.eventType.ACCOUNT_LOCKED,
    ProductHistoryListResponseDto.eventType.ACCOUNT_UNLOCKED,
    ProductHistoryListResponseDto.eventType.PASSWORD_RESET_BY_ADMIN,
    ProductHistoryListResponseDto.eventType.PASSWORD_CHANGED,
    ProductHistoryListResponseDto.eventType.FORCED_PASSWORD_CHANGE,
  ]);

export function isAccountSecurityEventType(
  eventType: ProductHistoryListResponseDto.eventType,
): boolean {
  return ACCOUNT_SECURITY_EVENT_TYPES.has(eventType);
}

/**
 * Format date for display
 */
export const formatHistoryDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/** Product codes from grouped document events (`newValue.products`). */
export function getHistoryProductCodesFromNewValue(
  newValue: Record<string, unknown> | Record<string, any> | undefined,
): string[] {
  const products = newValue?.products;
  if (!Array.isArray(products)) {
    return [];
  }

  const codes: string[] = [];
  for (const row of products) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const item = row as Record<string, unknown>;
    const unique = typeof item.productCodeUnique === 'string' ? item.productCodeUnique.trim() : '';
    const catalog = typeof item.productCode === 'string' ? item.productCode.trim() : '';
    const code = unique || catalog;
    if (code) {
      codes.push(code);
    }
  }
  return codes;
}
